import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatProgressBar } from '@angular/material/progress-bar';
import {
  RouterOutlet,
  Router,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
} from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { AccountService } from '@core/auth';
import { GpfLoadingComponent } from '@layout/ui/gpf-loading/gpf-loading.component';
import { environment } from '../environments/environment';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    GpfLoadingComponent,
    MatProgressBar,
    TranslocoModule,
    MatButton,
  ],
  host: {
    // Ensure root component fills the entire viewport
    class: 'flex min-h-full w-full flex-auto flex-col',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `@if (!production) {
      <div
        class="pointer-events-none fixed top-0 left-0 z-50 rounded-br bg-amber-9 px-2 py-0.5 font-mono text-xs font-bold text-black"
      >
        DEV
      </div>
    }
    @if (accountService.accountResource.isLoading()) {
      <div
        class="absolute top-0 right-0 bottom-0 left-0 z-50 flex items-center justify-center bg-white px-6"
      >
        <gpf-loading></gpf-loading>
      </div>
    } @else if (accountService.accountResource.error()) {
      <div
        class="absolute top-0 right-0 bottom-0 left-0 z-50 flex flex-col items-center justify-center bg-white"
        *transloco="let t"
      >
        <img
          class="mb-4 h-25 px-6"
          src="logo.svg"
          alt="logo"
        />
        <div class="my-3 text-center text-error">
          {{ t('SENTENCES.AN_ERROR_OCCURRED') }}
        </div>
        <div class="flex flex-row justify-center">
          <button
            matButton
            (click)="accountService.accountResource.reload()"
          >
            {{ t('SENTENCES.TRY_AGAIN') }}
          </button>
        </div>
      </div>
    } @else {
      @if (isNavigating()) {
        <mat-progress-bar
          class="fixed top-0 right-0 left-0 z-50 h-1"
          mode="determinate"
          [value]="navigationProgress()"
        />
      }
      <router-outlet />
    }`,
})
export class App {
  protected readonly accountService = inject(AccountService);
  protected readonly router = inject(Router);
  protected readonly production = environment.production;

  isNavigating = signal(false);
  navigationProgress = signal(0);

  private progressInterval: ReturnType<typeof setInterval> | null = null;

  private startProgress() {
    this.isNavigating.set(true);
    this.navigationProgress.set(0);

    this.progressInterval = setInterval(() => {
      const current = this.navigationProgress();
      if (current < 90) {
        // Slow down as we approach 90%
        const increment = (90 - current) * 0.1;
        this.navigationProgress.set(current + increment);
      }
    }, 100);
  }

  private completeProgress() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }

    this.navigationProgress.set(100);
    setTimeout(() => {
      this.isNavigating.set(false);
      this.navigationProgress.set(0);
    }, 200);
  }

  constructor() {
    if (!this.production) document.title = `[DEV] ${document.title}`;

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        this.startProgress();
      } else if (
        event instanceof NavigationEnd ||
        event instanceof NavigationCancel ||
        event instanceof NavigationError
      ) {
        this.completeProgress();
      }
    });
  }
}
